import type { FC } from "react"

import { useLineGroupMappingsEdit } from "./useLineGroupMappingsEdit"

import styles from "../GeneralInfoTab.module.scss"

// 会社単位で Fin URL を生成できるようになったため一時的にコメントアウト。
// 再開する場合は本ブロックと下記の URL 生成ボタン・generatedUrls 表示を戻して
// `yarn souhl-admin:codegen` を再実行する。
// const GenerateFinLoginUrl = graphql(/* GraphQL */ `
// 	mutation GenerateFinLoginUrlForLineGroupSection(
// 		$input: GenerateFinLoginUrlInput!
// 	) {
// 		generateFinLoginUrl(input: $input) {
// 			url
// 			expireTime
// 		}
// 	}
// `)

type LineGroup = {
	id: string
	name: string
}

// type GeneratedUrl = {
// 	lineGroupId: string
// 	url: string
// 	expireTime: string
// }

type LineGroupMappingsSectionProps = {
	companyId: string
	lineGroups: readonly LineGroup[]
}

const LineGroupMappingsSection: FC<LineGroupMappingsSectionProps> = ({
	companyId,
	lineGroups,
}) => {
	const edit = useLineGroupMappingsEdit(companyId, lineGroups)

	// const [generatedUrls, setGeneratedUrls] = useState<GeneratedUrl[]>([])
	// const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
	// const [generatingLineGroupId, setGeneratingLineGroupId] = useState<
	// 	string | null
	// >(null)

	// const [generateFinLoginUrl, { loading: generating }] =
	// 	useMutation(GenerateFinLoginUrl)

	// const handleGenerateUrl = async (lineGroupId: string) => {
	// 	setGeneratingLineGroupId(lineGroupId)
	// 	try {
	// 		const { data } = await generateFinLoginUrl({
	// 			variables: {
	// 				input: { lineGroupId },
	// 			},
	// 		})
	// 		const result = data?.generateFinLoginUrl
	// 		if (!result) return
	//
	// 		setGeneratedUrls((prev) => [
	// 			...prev,
	// 			{
	// 				lineGroupId,
	// 				url: result.url,
	// 				expireTime: new Date(result.expireTime).toLocaleDateString("ja-JP"),
	// 			},
	// 		])
	// 	} catch (error) {
	// 		console.error("URL生成エラー:", error)
	// 	} finally {
	// 		setGeneratingLineGroupId(null)
	// 	}
	// }

	// const handleCopyUrl = async (url: string) => {
	// 	await navigator.clipboard.writeText(url)
	// 	setCopiedUrl(url)
	// 	setTimeout(() => setCopiedUrl(null), 2000)
	// }

	const currentCount = lineGroups.length
	const draftCount = edit.draftDisplayNames.length
	const showDelta = edit.editing && draftCount !== currentCount

	return (
		<div className={styles.formContainer}>
			<div className={styles.formHeader}>
				<h2 className={styles.formTitle}>
					{showDelta
						? `LINE グループ (${currentCount}件 → ${draftCount}件)`
						: `LINE グループ (${currentCount}件)`}
				</h2>
				{edit.editing ? (
					<div className={styles.editActions}>
						<button
							type="button"
							className={styles.cancelButton}
							onClick={edit.cancelEdit}
							disabled={edit.saving}
						>
							キャンセル
						</button>
						<button
							type="button"
							className={styles.editButton}
							onClick={edit.save}
							disabled={edit.saving}
						>
							{edit.saving ? "保存中..." : "保存"}
						</button>
					</div>
				) : (
					<button
						type="button"
						className={styles.editButton}
						onClick={edit.startEdit}
					>
						編集
					</button>
				)}
			</div>

			<div className={styles.lineGroupList}>
				{edit.editing ? (
					<>
						<div className={styles.editNotice}>
							保存ボタンを押すと、ここに表示されているグループ名
							で紐付けが上書き保存されます。
						</div>
						{edit.draftDisplayNames.length === 0 ? (
							<div className={styles.emptyMessage}>
								紐付けられた LINE グループはありません
							</div>
						) : (
							<table className={styles.lineGroupTable}>
								<thead>
									<tr>
										<th className={styles.tableHeader}>グループ名</th>
										<th className={styles.tableHeaderAction}>アクション</th>
									</tr>
								</thead>
								<tbody>
									{edit.draftDisplayNames.map((displayName) => (
										<tr key={displayName} className={styles.tableRow}>
											<td className={styles.tableCell}>{displayName}</td>
											<td className={styles.tableCellAction}>
												<button
													type="button"
													className={styles.removeButton}
													onClick={() => edit.removeDisplayName(displayName)}
													disabled={edit.saving}
												>
													削除
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}

						<div className={styles.addRow}>
							<input
								type="text"
								className={styles.addInput}
								placeholder="グループ名を入力"
								aria-label="グループ名を入力"
								value={edit.newDisplayName}
								onChange={(e) => edit.setNewDisplayName(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault()
										edit.addDisplayName()
									}
								}}
								disabled={edit.saving}
							/>
							<button
								type="button"
								className={styles.addButton}
								onClick={edit.addDisplayName}
								disabled={edit.saving || edit.newDisplayName.trim() === ""}
							>
								追加
							</button>
						</div>

						{edit.inlineError && (
							<div className={styles.editError}>{edit.inlineError}</div>
						)}
					</>
				) : currentCount === 0 ? (
					<div className={styles.emptyMessage}>
						紐付けられている LINE グループはありません
					</div>
				) : (
					<table className={styles.lineGroupTable}>
						<thead>
							<tr>
								<th className={styles.tableHeader}>グループ名</th>
								<th className={styles.tableHeaderAction}>アクション</th>
							</tr>
						</thead>
						<tbody>
							{lineGroups.map((lineGroup) => (
								<tr key={lineGroup.id} className={styles.tableRow}>
									<td className={styles.tableCell}>{lineGroup.name}</td>
									<td className={styles.tableCellAction}>
										<a
											href={`https://talk.worksmobile.com/#/?lineGroupId=${lineGroup.id}`}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.generateButton}
										>
											LINE Works へ移動
										</a>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}

				{/*
				{!edit.editing && generatedUrls.length > 0 && (
					<div className={styles.generatedUrlsSection}>
						{generatedUrls.map((item, index) => (
							<div key={index} className={styles.generatedUrlItem}>
								<span className={styles.generatedUrlText}>
									{item.url} (有効期限: {item.expireTime})
								</span>
								<button
									type="button"
									className={styles.copyButton}
									onClick={async () => await handleCopyUrl(item.url)}
								>
									{copiedUrl === item.url ? "コピー済み" : "コピー"}
								</button>
							</div>
						))}
					</div>
				)}
				*/}
			</div>
		</div>
	)
}

export default LineGroupMappingsSection
